import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import { useAuth } from "@bw/shared/hooks";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useGuardianLinkStatus, useGuardianLookOnly } from "../../common/query/useGuardianLink";

/**
 * Persistent look-only banner (#2691). A minor with no active guardian, or one
 * whose guardian paused the account, can browse the app; this banner says why
 * booking, joining, posting and messaging wait, shows where the guardian request
 * stands, and links to the guardian-linking screen.
 */
export function GuardianNotice() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { lookOnly, paused, known } = useGuardianLookOnly(Boolean(user));
  const link = useGuardianLinkStatus(lookOnly);
  // While a possible minor's controls load, say nothing rather than guess
  // between "waiting for approval" and "paused".
  if (!lookOnly || !known) return null;

  const pending = link.data?.status === "pending";
  const onLinkScreen = pathname === "/me/guardian";

  return (
    <Alert
      severity="info"
      // A long guardian email must wrap on a phone, not run past the edge.
      sx={{ mb: 2, "& .MuiAlert-message": { minWidth: 0, overflowWrap: "anywhere" } }}
      action={
        paused || onLinkScreen ? null : (
          <Button component={RouterLink} to="/me/guardian" color="inherit" size="small" sx={{ whiteSpace: "nowrap" }}>
            {pending ? "See request" : "Ask a guardian"}
          </Button>
        )
      }
    >
      <AlertTitle>
        {paused ? "A guardian has paused your account" : "Ask a parent or guardian to approve your account"}
      </AlertTitle>
      {paused
        ? "You can look around. Booking, joining, posting and messages wait until a guardian turns your account back on."
        : pending
          ? `We asked ${link.data?.parent_email ?? "your parent or guardian"}. You can look around until they approve.`
          : "You can look around. Booking, joining, posting and messages wait until a parent or guardian approves."}
    </Alert>
  );
}
